import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Package, CloudDownload, Settings, Check, X, Trash2, Plus, Pencil, Copy, Upload, ImageOff, FolderOpen, Power, FileText, FileDown, Clock, BarChart3, Inbox, Mail, RefreshCw, PackageCheck, PackageX, Bell, DatabaseBackup, RotateCcw, AlertTriangle } from "lucide-react";
import { api, imgUrl, errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TABS = [
  { id: "users", labelKey: "admin.tab.users", icon: Users },
  { id: "requests", labelKey: "admin.tab.requests", icon: Inbox },
  { id: "products", labelKey: "admin.tab.products", icon: Package },
  { id: "incomplete", labelKey: "admin.tab.incomplete", icon: AlertTriangle },
  { id: "proformas", labelKey: "admin.tab.proformas", icon: FileText },
  { id: "stats", labelKey: "admin.tab.stats", icon: BarChart3 },
  { id: "emails", labelKey: "admin.tab.emails", icon: Mail },
  { id: "import", labelKey: "admin.tab.import", icon: CloudDownload },
  { id: "settings", labelKey: "admin.tab.settings", icon: Settings },
];

export default function AdminPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const visibleTabs = user?.role === "staff" ? TABS.filter((tb) => ["products", "incomplete"].includes(tb.id)) : TABS;
  const [tab, setTab] = useState(user?.role === "staff" ? "products" : "users");
  const [incompleteCount, setIncompleteCount] = useState(null);
  useEffect(() => { api.get("/admin/products/incomplete-count").then(({ data }) => setIncompleteCount(data.count)).catch(() => {}); }, [tab]);
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="admin-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading">{t("admin.title")}</h1>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {visibleTabs.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`admin-tab-${tb.id}`}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium border transition-colors ${tab === tb.id ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
            <tb.icon size={15} /> {t(tb.labelKey)}
            {tb.id === "incomplete" && incompleteCount > 0 && <span className="bg-red-600 text-white text-[10px] rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center font-bold" data-testid="incomplete-count-badge">{incompleteCount}</span>}
          </button>
        ))}
      </div>
      {tab === "users" && <UsersTab />}
      {tab === "requests" && <RequestsTab />}
      {tab === "products" && <ProductsTab />}
      {tab === "incomplete" && <ProductsTab incompleteOnly />}
      {tab === "proformas" && <ProformasTab />}
      {tab === "stats" && <StatsTab />}
      {tab === "emails" && <EmailLogsTab />}
      {tab === "import" && <ImportTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

const STATUS_BADGE = {
  pending: "bg-amber-500/15 text-amber-600",
  approved: "bg-emerald-500/15 text-emerald-600",
  rejected: "bg-red-500/15 text-red-500",
};
const STATUS_LABEL = { pending: "Pendiente", approved: "Aprobado", rejected: "Rechazado" };

const EMPTY_USER_FORM = { name: "", company: "", email: "", phone: "", country: "", role: "client", allowed_categories: [], password: "", use_amount: "", use_unit: "dias" };

const fmtExpiry = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const computeExpiry = (amount, unit) => {
  const n = parseInt(amount, 10);
  if (!n || n <= 0) return null;
  const mult = { minutos: 60000, horas: 3600000, dias: 86400000 }[unit] || 86400000;
  return new Date(Date.now() + n * mult).toISOString();
};

const isExpired = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d) && d < new Date();
};

const COUNTRIES = [
  "BELICE", "COLOMBIA", "COSTA RICA", "CURAZAO", "ECUADOR", "EL SALVADOR",
  "GUATEMALA", "GUYANA", "HAITI", "HONDURAS", "ISLAS VIRGENES", "JAMAICA",
  "MEXICO", "NICARAGUA", "PANAMA / ZONA LIBRE", "REP. DOMINICANA", "ST. LUCIA",
  "ST. VINCENT", "SURINAME", "TRINIDAD", "VENEZUELA",
];

const CLIENT_TYPE_LABEL = { contado: "Contado", credito: "Crédito", cod: "COD" };

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_USER_FORM);
  const [created, setCreated] = useState(null);
  const [categories, setCategories] = useState([]);
  const [catUser, setCatUser] = useState(null);
  const [catSel, setCatSel] = useState([]);
  const [expUser, setExpUser] = useState(null);
  const [expAmount, setExpAmount] = useState("");
  const [expUnit, setExpUnit] = useState("dias");
  const [countrySel, setCountrySel] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editCountrySel, setEditCountrySel] = useState("");

  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(errDetail(e))), []);
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        password: form.password || null,
        access_expires_at: form.role === "client" ? computeExpiry(form.use_amount, form.use_unit) : null,
        client_type: form.role === "client" ? form.client_type : null,
      };
      const { data } = await api.post("/admin/users", payload);
      setCreated({ email: form.email, password: data.generated_password });
      setForm(EMPTY_USER_FORM);
      setCountrySel("");
      load();
      toast.success("Usuario registrado");
    } catch (err) { toast.error(errDetail(err)); }
  };

  const openCategories = (u) => { setCatUser(u); setCatSel(u.allowed_categories || []); };

  const openExpiry = (u) => {
    setExpUser(u);
    setExpAmount("");
    setExpUnit("dias");
  };

  const saveExpiry = async () => {
    try {
      await api.put(`/admin/users/${expUser.id}/expiry`, {
        access_expires_at: computeExpiry(expAmount, expUnit),
      });
      setExpUser(null); load();
      toast.success("Tiempo de acceso actualizado");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setEditForm({
      id: u.id, name: u.name || "", email: u.email || "", phone: u.phone || "",
      country: u.country || "", role: u.role || "client",
      client_type: u.client_type || "contado", password: "",
      allowed_categories: u.allowed_categories || [],
    });
    if (!u.country) setEditCountrySel("");
    else if (COUNTRIES.includes(u.country)) setEditCountrySel(u.country);
    else setEditCountrySel("OTRO");
  };

  const saveEditUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: editForm.name, email: editForm.email, phone: editForm.phone, role: editForm.role,
        country: editForm.role === "client" ? editForm.country : "",
        client_type: editForm.role === "client" ? editForm.client_type : null,
        allowed_categories: editForm.role === "client" ? editForm.allowed_categories : [],
        password: editForm.password || null,
      };
      await api.put(`/admin/users/${editForm.id}`, payload);
      setEditUser(null); load();
      toast.success("Usuario actualizado");
    } catch (err) { toast.error(errDetail(err)); }
  };

  const setClientType = async (u, ct) => {
    try {
      await api.put(`/admin/users/${u.id}/client-type`, { client_type: ct });
      load();
      toast.success(`Marcado como Cliente ${ct === "credito" ? "Crédito" : "Contado"}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveCategories = async () => {
    try {
      await api.put(`/admin/users/${catUser.id}/categories`, { allowed_categories: catSel });
      setCatUser(null); load();
      toast.success("Carpetas asignadas actualizadas");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/users/${id}/status`, { status });
      load();
      toast.success(status === "approved" ? "Usuario aprobado" : "Usuario rechazado");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setActive = async (u, active) => {
    try {
      await api.put(`/admin/users/${u.id}/active`, { active });
      load();
      toast.success(active ? "Usuario activado" : "Usuario desactivado");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/users/${id}`); load(); toast.success("Usuario eliminado"); } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-4">
      <button onClick={() => { setOpen(true); setCreated(null); setForm(EMPTY_USER_FORM); setCountrySel(""); }} data-testid="admin-create-user-button"
        className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold hover:opacity-90">
        <Plus size={16} /> Registrar usuario
      </button>

      {/* Desktop table */}
      <div className="hidden md:block bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Nombre</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Rol</th>
              <th className="px-4 py-3 font-semibold">Carpetas</th>
              <th className="px-4 py-3 font-semibold">Estado</th>
              <th className="px-4 py-3 font-semibold">Acceso</th>
              <th className="px-4 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={`border-t border-border ${u.active === false ? "opacity-60" : ""}`} data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}<p className="text-xs text-muted-foreground font-normal">{u.phone}</p></td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    <RoleBadge role={u.role} email={u.email} />
                    {u.role === "client" && (
                      <select value={u.client_type || "contado"} onChange={(e) => setClientType(u, e.target.value)}
                        data-testid={`client-type-select-${u.email}`}
                        className="text-[11px] rounded-full border border-border bg-background px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-600">
                        <option value="contado">Contado</option>
                        <option value="credito">Crédito</option>
                        <option value="cod">COD</option>
                      </select>
                    )}
                    {u.country && <span className="text-[11px] text-muted-foreground">{u.country}</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {u.role === "client" ? (
                    <button onClick={() => openCategories(u)} data-testid={`edit-categories-${u.email}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-medium hover:bg-muted">
                      <FolderOpen size={13} className="text-amber-600" />
                      {(u.allowed_categories || []).length === 0 ? "Todas" : `${u.allowed_categories.length} carpetas`}
                    </button>
                  ) : <span className="text-xs text-muted-foreground">Acceso total</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[u.status]}`} data-testid={`user-status-${u.email}`}>{STATUS_LABEL[u.status]}</span>
                    {u.renewal_requested && <span data-testid={`renewal-badge-${u.email}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/15 text-sky-600">RENOVACIÓN SOLICITADA</span>}
                    {u.role === "client" && isExpired(u.access_expires_at) && <span data-testid={`expired-badge-${u.email}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/15 text-red-500">EXPIRADO</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    <ActiveToggle u={u} setActive={setActive} />
                    {u.role === "client" && (
                      <button onClick={() => openExpiry(u)} data-testid={`edit-expiry-${u.email}`}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${isExpired(u.access_expires_at) ? "border-red-500/50 text-red-500" : u.access_expires_at ? "border-amber-500/40 text-amber-600" : "border-border text-muted-foreground"} hover:bg-muted`}>
                        <Clock size={11} /> {u.access_expires_at ? (isExpired(u.access_expires_at) ? "Expirado" : fmtExpiry(u.access_expires_at)) : "Sin límite"}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <UserActions u={u} setStatus={setStatus} remove={remove} onEdit={openEditUser} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p className="text-center py-10 text-muted-foreground text-sm">Sin usuarios registrados.</p>}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {users.map((u) => (
          <div key={u.id} className={`bg-card border border-border rounded-2xl p-4 space-y-2 ${u.active === false ? "opacity-60" : ""}`} data-testid={`user-card-${u.email}`}>
            <div className="flex items-center justify-between">
              <p className="font-semibold">{u.name}</p>
              <div className="flex flex-col items-end gap-1">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[u.status]}`}>{STATUS_LABEL[u.status]}</span>
                {u.renewal_requested && <span data-testid={`renewal-badge-mobile-${u.email}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/15 text-sky-600">RENOVACIÓN</span>}
                {u.role === "client" && isExpired(u.access_expires_at) && <span data-testid={`expired-badge-mobile-${u.email}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/15 text-red-500">EXPIRADO</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap"><RoleBadge role={u.role} email={u.email} />
              <ActiveToggle u={u} setActive={setActive} />
              {u.role === "client" && (
                <select value={u.client_type || "contado"} onChange={(e) => setClientType(u, e.target.value)}
                  data-testid={`client-type-select-mobile-${u.email}`}
                  className="text-xs rounded-full border border-border bg-background px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-600">
                  <option value="contado">Contado</option>
                  <option value="credito">Crédito</option>
                  <option value="cod">COD</option>
                </select>
              )}
              {u.role === "client" && (
                <button onClick={() => openCategories(u)} className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-border text-xs font-medium hover:bg-muted">
                  <FolderOpen size={12} className="text-amber-600" />
                  {(u.allowed_categories || []).length === 0 ? "Todas" : `${u.allowed_categories.length} carpetas`}
                </button>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{u.email} · {u.phone}</p>
            {u.role === "client" && (
              <button onClick={() => openExpiry(u)} data-testid={`edit-expiry-mobile-${u.email}`}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border w-fit ${isExpired(u.access_expires_at) ? "border-red-500/50 text-red-500" : u.access_expires_at ? "border-amber-500/40 text-amber-600" : "border-border text-muted-foreground"}`}>
                <Clock size={12} /> {u.access_expires_at ? (isExpired(u.access_expires_at) ? "Acceso expirado" : `Acceso hasta ${fmtExpiry(u.access_expires_at)}`) : "Acceso sin límite"}
              </button>
            )}
            <UserActions u={u} setStatus={setStatus} remove={remove} onEdit={openEditUser} />
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Registrar nuevo usuario</DialogTitle></DialogHeader>
          {created ? (
            <div className="space-y-4" data-testid="created-user-credentials">
              <p className="text-sm">Usuario creado. Comparta estas credenciales manualmente:</p>
              <div className="bg-muted rounded-xl p-4 font-mono text-sm space-y-1">
                <p><b>Email:</b> {created.email}</p>
                <p><b>Contraseña:</b> <span data-testid="generated-password">{created.password}</span></p>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(`Email: ${created.email}\nContraseña: ${created.password}`); toast.success("Copiado"); }}
                data-testid="copy-credentials-button"
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:bg-muted">
                <Copy size={14} /> Copiar credenciales
              </button>
              <p className="text-xs text-amber-600">Recuerde aprobar al usuario para que pueda ver el catálogo.</p>
            </div>
          ) : (
            <form onSubmit={createUser} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <label className="text-sm font-medium mb-1 block">Tipo de acceso</label>
                <div className="flex gap-2" data-testid="create-user-role-selector">
                  {[["client", "Cliente"], ["staff", "Personal (solo productos)"], ["admin", "Administración de la plataforma"]].map(([r, label]) => (
                    <button type="button" key={r} onClick={() => setForm({ ...form, role: r })} data-testid={`role-option-${r}`}
                      className={`flex-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${form.role === r ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {[["name", "Nombre completo"], ["email", "Email"], ["phone", "Teléfono"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-sm font-medium mb-1 block">{label}</label>
                  <input required={k === "name" || k === "email"} type={k === "email" ? "email" : "text"}
                    value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`create-user-${k}-input`}
                    className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                </div>
              ))}
              {form.role === "client" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">País</label>
                  <select value={countrySel}
                    onChange={(e) => { const v = e.target.value; setCountrySel(v); setForm({ ...form, country: v === "OTRO" ? "" : v }); }}
                    data-testid="create-user-country-select"
                    className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
                    <option value="">Seleccione un país</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value="OTRO">OTRO (ESCRIBIR MANUALMENTE)</option>
                  </select>
                  {countrySel === "OTRO" && (
                    <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                      data-testid="create-user-country-other-input" placeholder="Escriba el país"
                      className="mt-2 w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                  )}
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">Contraseña</label>
                <input type="text" minLength={6} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="create-user-password-input"
                  placeholder="Dejar vacío para generarla automáticamente"
                  className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                <p className="text-xs text-muted-foreground mt-1">Mínimo 6 caracteres. Si se deja vacío, el sistema genera una automáticamente.</p>
              </div>
              {form.role === "client" && (
                <CategoryPicker categories={categories} selected={form.allowed_categories}
                  onChange={(sel) => setForm({ ...form, allowed_categories: sel })} />
              )}
              {form.role === "client" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Tipo de cliente</label>
                  <div className="flex gap-2" data-testid="create-user-client-type-selector">
                    {[["contado", "Contado"], ["credito", "Crédito"], ["cod", "COD"]].map(([v, label]) => (
                      <button type="button" key={v} onClick={() => setForm({ ...form, client_type: v })}
                        data-testid={`client-type-option-${v}`}
                        className={`flex-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${form.client_type === v ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {form.role === "client" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Tiempo de uso de la plataforma</label>
                  <div className="flex gap-2">
                    <input type="number" min="1" value={form.use_amount}
                      onChange={(e) => setForm({ ...form, use_amount: e.target.value })}
                      data-testid="create-user-use-amount-input" placeholder="Ej. 30"
                      className="w-28 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                    <select value={form.use_unit} onChange={(e) => setForm({ ...form, use_unit: e.target.value })}
                      data-testid="create-user-use-unit-select"
                      className="flex-1 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
                      <option value="minutos">Minutos</option>
                      <option value="horas">Horas</option>
                      <option value="dias">Días</option>
                    </select>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Opcional. El tiempo se cuenta desde ahora. Al agotarse, el cliente ya no podrá iniciar sesión ni usar la plataforma. Dejar vacío = acceso sin límite.</p>
                </div>
              )}
              {form.role === "admin" && (
                <p className="text-xs text-amber-600">Los administradores tienen acceso total a la plataforma y quedan aprobados automáticamente.</p>
              )}
              {form.role === "staff" && (
                <p className="text-xs text-violet-600">El personal solo puede subir/editar productos y ver el catálogo. No tiene acceso al carrito de compras ni a la administración de usuarios.</p>
              )}
              <button type="submit" data-testid="create-user-submit-button"
                className="w-full py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold">Registrar</button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!catUser} onOpenChange={(o) => !o && setCatUser(null)}>
        <DialogContent data-testid="categories-dialog">
          <DialogHeader><DialogTitle>Carpetas visibles — {catUser?.name}</DialogTitle></DialogHeader>
          <CategoryPicker categories={categories} selected={catSel} onChange={setCatSel} />
          <button onClick={saveCategories} data-testid="save-categories-button"
            className="w-full py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold">Guardar</button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!expUser} onOpenChange={(o) => !o && setExpUser(null)}>
        <DialogContent data-testid="expiry-dialog">
          <DialogHeader><DialogTitle>Tiempo de acceso — {expUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Actual: {expUser?.access_expires_at ? `acceso hasta ${fmtExpiry(expUser.access_expires_at)}` : "sin límite"}
            </p>
            <label className="text-sm font-medium block">Nueva duración (desde ahora)</label>
            <div className="flex gap-2">
              <input type="number" min="1" value={expAmount} onChange={(e) => setExpAmount(e.target.value)}
                data-testid="expiry-amount-input" placeholder="Ej. 30"
                className="w-28 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              <select value={expUnit} onChange={(e) => setExpUnit(e.target.value)}
                data-testid="expiry-unit-select"
                className="flex-1 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
                <option value="minutos">Minutos</option>
                <option value="horas">Horas</option>
                <option value="dias">Días</option>
              </select>
            </div>
            <p className="text-xs text-muted-foreground">Dejar el número vacío o en 0 = quitar el límite (acceso sin límite).</p>
          </div>
          <button onClick={saveExpiry} data-testid="save-expiry-button"
            className="w-full py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold">Guardar</button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Editar usuario</DialogTitle></DialogHeader>
          {editForm && (
            <form onSubmit={saveEditUser} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <label className="text-sm font-medium mb-1 block">Tipo de acceso</label>
                <div className="flex gap-2" data-testid="edit-user-role-selector">
                  {[["client", "Cliente"], ["staff", "Personal (solo productos)"], ["admin", "Administración de la plataforma"]].map(([r, label]) => (
                    <button type="button" key={r} onClick={() => setEditForm({ ...editForm, role: r })} data-testid={`edit-role-option-${r}`}
                      className={`flex-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${editForm.role === r ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {[["name", "Nombre completo"], ["email", "Email"], ["phone", "Teléfono"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-sm font-medium mb-1 block">{label}</label>
                  <input required={k === "name" || k === "email"} type={k === "email" ? "email" : "text"}
                    value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                    data-testid={`edit-user-${k}-input`}
                    className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                </div>
              ))}
              {editForm.role === "client" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">País</label>
                  <select value={editCountrySel}
                    onChange={(e) => { const v = e.target.value; setEditCountrySel(v); setEditForm({ ...editForm, country: v === "OTRO" ? "" : v }); }}
                    data-testid="edit-user-country-select"
                    className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
                    <option value="">Seleccione un país</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value="OTRO">OTRO (ESCRIBIR MANUALMENTE)</option>
                  </select>
                  {editCountrySel === "OTRO" && (
                    <input value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                      data-testid="edit-user-country-other-input" placeholder="Escriba el país"
                      className="mt-2 w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                  )}
                </div>
              )}
              {editForm.role === "client" && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Tipo de cliente</label>
                  <div className="flex gap-2" data-testid="edit-user-client-type-selector">
                    {[["contado", "Contado"], ["credito", "Crédito"], ["cod", "COD"]].map(([v, label]) => (
                      <button type="button" key={v} onClick={() => setEditForm({ ...editForm, client_type: v })}
                        data-testid={`edit-client-type-option-${v}`}
                        className={`flex-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${editForm.client_type === v ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {editForm.role === "client" && (
                <CategoryPicker categories={categories} selected={editForm.allowed_categories}
                  onChange={(sel) => setEditForm({ ...editForm, allowed_categories: sel })} />
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">Nueva contraseña</label>
                <input type="text" minLength={6} value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  data-testid="edit-user-password-input"
                  placeholder="Dejar vacío para no cambiarla"
                  className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
              <p className="text-xs text-muted-foreground">El tiempo de acceso se edita desde el control de la lista.</p>
              <button type="submit" data-testid="save-edit-user-button"
                className="w-full py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold">Guardar cambios</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActiveToggle({ u, setActive }) {
  const active = u.active !== false;
  return (
    <button onClick={() => setActive(u, !active)} data-testid={`toggle-active-${u.email}`}
      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${active ? "bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25" : "bg-red-500/15 text-red-500 hover:bg-red-500/25"}`}>
      <Power size={12} /> {active ? "Activado" : "Desactivado"}
    </button>
  );
}

function StatsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)).catch((e) => toast.error(errDetail(e))); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Cargando estadísticas...</p>;
  const Bar = ({ label, value, max, suffix }) => (
    <div className="space-y-1" data-testid="stat-bar">
      <div className="flex justify-between text-xs gap-2"><span className="font-medium truncate">{label}</span><span className="text-muted-foreground whitespace-nowrap">{suffix}</span></div>
      <div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${max ? Math.max(4, (value / max) * 100) : 0}%` }} /></div>
    </div>
  );
  const maxType = Math.max(1, ...data.client_types.map((x) => x.total));
  const maxCountry = Math.max(1, ...data.countries.map((x) => x.total));
  const maxProd = Math.max(1, ...data.top_products.map((x) => x.boxes));
  return (
    <div className="space-y-6" data-testid="stats-tab">
      <p className="text-sm text-muted-foreground">Basado en {data.proformas_count} proformas (excluye anuladas).</p>
      <section className="bg-card border border-border rounded-2xl p-4 space-y-3">
        <h3 className="font-semibold">Clientes por tipo</h3>
        {data.client_types.map((c) => <Bar key={c.client_type} label={c.label} value={c.total} max={maxType} suffix={`$${c.total.toLocaleString()} · ${c.count} prof.`} />)}
      </section>
      <section className="bg-card border border-border rounded-2xl p-4 space-y-3">
        <h3 className="font-semibold">Compras por país</h3>
        {data.countries.length ? data.countries.map((c) => <Bar key={c.country} label={c.country} value={c.total} max={maxCountry} suffix={`$${c.total.toLocaleString()} · ${c.count}`} />) : <p className="text-sm text-muted-foreground">Sin datos.</p>}
      </section>
      <section className="bg-card border border-border rounded-2xl p-4 space-y-3">
        <h3 className="font-semibold">Top 25 productos (cajas vendidas)</h3>
        {data.top_products.length ? data.top_products.map((p, i) => <Bar key={p.reference || i} label={`${i + 1}. ${p.reference} ${p.name}`} value={p.boxes} max={maxProd} suffix={`${p.boxes} cajas`} />) : <p className="text-sm text-muted-foreground">Sin datos.</p>}
      </section>
    </div>
  );
}

function EmailLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/email-logs").then(({ data }) => setLogs(data)).catch((e) => toast.error(errDetail(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const fmt = (iso) => new Date(iso).toLocaleString("es-PA", { timeZone: "America/Panama", dateStyle: "short", timeStyle: "short" });
  return (
    <div data-testid="email-logs-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Correos enviados <span className="text-sm text-muted-foreground font-normal">({logs.length})</span></h2>
        <button onClick={load} data-testid="email-logs-refresh" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border text-sm hover:bg-muted">
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Cargando...</p> : logs.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="email-logs-empty">Aún no se han enviado correos.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="email-logs-table">
            <thead className="bg-muted/60 text-left">
              <tr><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Destinatario</th><th className="px-3 py-2">Asunto</th><th className="px-3 py-2">Estado</th></tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l._id} className="border-t border-border" data-testid={`email-log-row-${l._id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(l.created_at)}</td>
                  <td className="px-3 py-2 break-all">{l.to.join(", ")}</td>
                  <td className="px-3 py-2">{l.subject}</td>
                  <td className="px-3 py-2">
                    <span title={l.error || ""} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${l.status === "enviado" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}>
                      {l.status === "enviado" ? "Enviado" : "Error"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RequestsTab() {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [clientType, setClientType] = useState("contado");
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("dias");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/requests").then(({ data }) => setReqs(data)).catch((e) => toast.error(errDetail(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openActivate = (u) => { setTarget(u); setClientType(u.client_type || "contado"); setAmount(""); setUnit("dias"); setResult(null); };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/users/${target.id}/activate`, {
        client_type: clientType,
        access_expires_at: computeExpiry(amount, unit),
      });
      if (data.generated_password) {
        setResult({ email: target.email, password: data.generated_password });
        toast.success("Cuenta activada y credenciales enviadas por email");
      } else {
        toast.success("Cuenta activada");
        setTarget(null);
      }
      load();
    } catch (e) { toast.error(errDetail(e)); }
    setBusy(false);
  };

  const reject = async (u) => {
    if (!window.confirm(`¿Rechazar la solicitud de ${u.name || u.email}?`)) return;
    try {
      await api.put(`/admin/users/${u.id}/status`, { status: "rejected" });
      toast.success("Solicitud rechazada");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-4" data-testid="requests-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Solicitudes de registro pendientes de activación. Al activar se define el tipo de cliente y el tiempo de acceso, y se envían las credenciales por email.
        </p>
        <span data-testid="requests-count" className="shrink-0 ml-3 px-3 py-1 rounded-full bg-amber-500/15 text-amber-600 text-xs font-semibold">{reqs.length} pendientes</span>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando...</p>
      ) : reqs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="requests-empty">
          <Inbox size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No hay solicitudes pendientes.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {reqs.map((u) => (
            <div key={u.id} data-testid={`request-card-${u.email}`}
              className="bg-card border border-border rounded-2xl p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{u.name || "—"}</p>
                  <p className="text-xs text-muted-foreground break-all">{u.email}</p>
                </div>
                <span className="shrink-0 px-2 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-600">Pendiente</span>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {u.phone && <p><b>Teléfono:</b> {u.phone}</p>}
                <p><b>País:</b> {u.country || "—"}</p>
                <p><b>Categorías:</b> {(u.allowed_categories && u.allowed_categories.length) ? u.allowed_categories.join(", ") : "Todas"}</p>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => openActivate(u)} data-testid={`activate-request-${u.email}`}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                  <Check size={13} /> Activar y enviar credenciales
                </button>
                <button onClick={() => reject(u)} data-testid={`reject-request-${u.email}`}
                  className="flex items-center gap-1 px-3 py-2 rounded-full bg-red-500/10 text-red-500 text-xs font-semibold hover:bg-red-500/20">
                  <X size={13} /> Rechazar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="activate-request-dialog">
          <DialogHeader><DialogTitle>Activar cuenta</DialogTitle></DialogHeader>
          {target && !result && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{target.name} — {target.email}</p>
              <div>
                <label className="text-sm font-medium mb-1 block">Tipo de cliente</label>
                <div className="flex gap-2" data-testid="activate-client-type-selector">
                  {[["contado", "Contado"], ["credito", "Crédito"], ["cod", "COD"]].map(([v, label]) => (
                    <button type="button" key={v} onClick={() => setClientType(v)}
                      data-testid={`activate-client-type-${v}`}
                      className={`flex-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${clientType === v ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Tiempo de acceso (opcional)</label>
                <div className="flex gap-2">
                  <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
                    data-testid="activate-access-amount" placeholder="Ej: 30"
                    className="w-24 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                  <select value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="activate-access-unit"
                    className="flex-1 px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
                    <option value="minutos">Minutos</option>
                    <option value="horas">Horas</option>
                    <option value="dias">Días</option>
                  </select>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Vacío = acceso sin límite.</p>
              </div>
              <button onClick={activate} disabled={busy} data-testid="confirm-activate-button"
                className="w-full py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
                {busy ? "Activando..." : "Activar y enviar credenciales"}
              </button>
            </div>
          )}
          {result && (
            <div className="space-y-3" data-testid="activate-result">
              <p className="text-sm">Cuenta activada. Credenciales enviadas por email a <b>{result.email}</b>.</p>
              <div className="bg-muted rounded-xl p-3 text-sm">
                <p><b>Usuario:</b> {result.email}</p>
                <p className="flex items-center gap-2"><b>Contraseña:</b> <span className="font-mono">{result.password}</span>
                  <button onClick={() => { navigator.clipboard.writeText(result.password); toast.success("Copiado"); }}
                    data-testid="copy-generated-password" className="p-1 hover:text-amber-600"><Copy size={14} /></button>
                </p>
              </div>
              <button onClick={() => setTarget(null)} data-testid="close-activate-result"
                className="w-full py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold">Listo</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoleBadge({ role, email }) {
  const map = {
    admin: ["Administrador", "bg-sky-500/15 text-sky-600"],
    staff: ["Personal", "bg-violet-500/15 text-violet-600"],
    client: ["Cliente", "bg-slate-500/15 text-slate-500 dark:text-slate-300"],
  };
  const [label, cls] = map[role] || map.client;
  return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${cls}`} data-testid={`user-role-${email}`}>{label}</span>;
}

function CategoryPicker({ categories, selected, onChange }) {
  const all = selected.length === 0;
  const toggle = (c) => onChange(selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c]);
  return (
    <div className="space-y-2" data-testid="category-picker">
      <label className="text-sm font-medium block">Carpetas (categorías) que puede ver</label>
      <button type="button" onClick={() => onChange([])} data-testid="category-picker-all"
        className={`w-full px-3 py-2 rounded-xl border text-xs font-semibold text-left ${all ? "bg-emerald-600 text-white border-transparent" : "border-border hover:bg-muted"}`}>
        ✓ Todas las carpetas (acceso completo al catálogo)
      </button>
      <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto border border-border rounded-xl p-2">
        {categories.map((c) => (
          <label key={c} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer ${selected.includes(c) ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold" : "hover:bg-muted"}`}>
            <input type="checkbox" checked={selected.includes(c)} onChange={() => toggle(c)}
              data-testid={`category-checkbox-${c}`} className="accent-amber-600" />
            {c}
          </label>
        ))}
      </div>
      {!all && <p className="text-xs text-muted-foreground">{selected.length} carpetas seleccionadas. El cliente solo verá productos de estas categorías.</p>}
    </div>
  );
}

function UserActions({ u, setStatus, remove, onEdit }) {
  return (
    <div className="flex items-center gap-1.5 justify-end md:justify-end">
      <button onClick={() => onEdit(u)} data-testid={`edit-user-${u.email}`}
        className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900/5 dark:bg-white/10 text-xs font-semibold hover:bg-slate-900/10"><Pencil size={13} /> Editar</button>
      {u.status !== "approved" && (
        <button onClick={() => setStatus(u.id, "approved")} data-testid={`approve-user-${u.email}`}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"><Check size={13} /> Aprobar</button>
      )}
      {u.status !== "rejected" && (
        <button onClick={() => setStatus(u.id, "rejected")} data-testid={`reject-user-${u.email}`}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-500/10 text-red-500 text-xs font-semibold hover:bg-red-500/20"><X size={13} /> Rechazar</button>
      )}
      <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.email}`}
        className="p-2 text-muted-foreground hover:text-red-500 rounded-full"><Trash2 size={14} /></button>
    </div>
  );
}

function ProductsTab({ incompleteOnly = false }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ reference: "", name: "", category: "", price_dozen: 0, packing: 0, packing_unit: "doc", size: "", weight_kg: 0, cubic_ft: 0 });
  const [dialogFile, setDialogFile] = useState(null);
  const [dialogPreview, setDialogPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [imgTarget, setImgTarget] = useState(null);
  const [catOther, setCatOther] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  const load = useCallback(() => api.get("/products", { params: { search: search || undefined, category: catFilter || undefined, stock: stockFilter || undefined, incomplete: incompleteOnly || undefined } }).then(({ data }) => setProducts(data)).catch((e) => toast.error(errDetail(e))), [search, catFilter, stockFilter, incompleteOnly]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, [products]);

  const openEdit = (p) => {
    setEditing(p || "new");
    setDialogFile(null);
    setDialogPreview(null);
    setGalleryImages(p ? (p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : [])) : []);
    setForm(p ? { reference: p.reference, name: p.name, category: p.category, price_dozen: p.price_dozen, packing: p.packing, packing_unit: p.packing_unit || "doc", size: p.size || "", weight_kg: p.weight_kg || 0, cubic_ft: p.cubic_ft || 0 } : { reference: "", name: "", category: "", price_dozen: 0, packing: 0, packing_unit: "doc", size: "", weight_kg: 0, cubic_ft: 0 });
    setCatOther(p && p.category ? !categories.includes(p.category) : false);
  };

  const deleteGalleryImage = async (url) => {
    if (editing === "new") return;
    try {
      const { data } = await api.delete(`/admin/products/${editing.id}/image`, { data: { url } });
      setGalleryImages(data.images && data.images.length ? data.images : (data.image_url ? [data.image_url] : []));
      load();
      toast.success("Foto eliminada");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const addGalleryImage = async (file) => {
    if (!file || editing === "new") return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/products/${editing.id}/image`, fd);
      setGalleryImages(data.images && data.images.length ? data.images : (data.image_url ? [data.image_url] : []));
      load();
      toast.success("Foto agregada a la galería");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const reorderGallery = async (from, to) => {
    setDragIdx(null);
    if (from == null || from === to || editing === "new") return;
    const arr = [...galleryImages];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setGalleryImages(arr);
    try {
      const { data } = await api.put(`/admin/products/${editing.id}/images/reorder`, { images: arr });
      setGalleryImages(data.images && data.images.length ? data.images : arr);
      load();
      toast.success("Orden actualizado · la primera foto es la principal");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const pickDialogFile = (file) => {
    if (!file) return;
    setDialogFile(file);
    setDialogPreview(URL.createObjectURL(file));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, price_dozen: parseFloat(form.price_dozen) || 0, packing: parseFloat(form.packing) || 0 };
      let productId;
      if (editing === "new") {
        const { data } = await api.post("/admin/products", body);
        productId = data.id;
      } else {
        await api.put(`/admin/products/${editing.id}`, body);
        productId = editing.id;
      }
      if (dialogFile) {
        const fd = new FormData();
        fd.append("file", dialogFile);
        await api.post(`/admin/products/${productId}/image`, fd);
      }
      setEditing(null); load();
      toast.success(dialogFile ? "Producto guardado con imagen (ajustada a 600x600, máx. 100KB)" : "Producto guardado");
    } catch (err) { toast.error(errDetail(err)); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.delete(`/admin/products/${p.id}`); load(); toast.success("Producto eliminado"); } catch (e) { toast.error(errDetail(e)); }
  };

  const toggleStock = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}/stock`, { out_of_stock: !p.out_of_stock });
      load();
      toast.success(p.out_of_stock ? `${p.reference} marcado como disponible` : `${p.reference} marcado como agotado`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const uploadImage = async (files) => {
    if (!files || !files.length || !imgTarget) return;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/admin/products/${imgTarget}/image`, fd);
      }
      load(); toast.success(`${files.length} imagen(es) agregada(s) a la galería`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <button onClick={() => openEdit(null)} data-testid="admin-create-product-button"
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold hover:opacity-90 w-fit">
          <Plus size={16} /> Nuevo producto
        </button>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} data-testid="admin-product-stock-filter"
            className={`px-4 py-2 rounded-full border bg-background text-sm w-full sm:w-44 focus:outline-none focus:ring-2 focus:ring-amber-600 ${stockFilter === "out" ? "border-red-500 text-red-600 font-semibold" : "border-input"}`}>
            <option value="">Todo el stock</option>
            <option value="in">Solo disponibles</option>
            <option value="out">Solo agotados</option>
          </select>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="admin-product-category-filter"
            className="px-4 py-2 rounded-full border border-input bg-background text-sm w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-amber-600">
            <option value="">Todas las categorías</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar (ref, nombre o categoría)..." data-testid="admin-product-search-input"
            className="px-4 py-2 rounded-full border border-input bg-background text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-amber-600" />
        </div>
      </div>
      {incompleteOnly ? (
        <div className="flex items-center gap-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-2xl px-4 py-3" data-testid="incomplete-banner">
          <AlertTriangle size={18} className="text-red-600" />
          <p className="text-sm"><b data-testid="incomplete-count">{products.length}</b> producto(s) con información incompleta. Completa los campos en rojo con el botón de editar.</p>
        </div>
      ) : (
      <p className="text-xs text-muted-foreground">{products.length} productos</p>
      )}

      <div className="hidden md:block bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">Imagen</th>
              <th className="px-4 py-3 font-semibold">Referencia</th>
              <th className="px-4 py-3 font-semibold">Nombre</th>
              <th className="px-4 py-3 font-semibold">Categoría</th>
              <th className="px-4 py-3 font-semibold text-right">Precio/doc</th>
              <th className="px-4 py-3 font-semibold text-right">Empaque</th>
              {incompleteOnly && <th className="px-4 py-3 font-semibold">Campos faltantes</th>}
              <th className="px-4 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className={`border-t border-border ${p.out_of_stock ? "opacity-70" : ""}`} data-testid={`admin-product-row-${p.reference}`}>
                <td className="px-4 py-2">
                  {p.image_url ? <img src={imgUrl(p.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center"><ImageOff size={14} /></div>}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{p.reference}</td>
                <td className="px-4 py-2">{p.name} {p.out_of_stock && <OutOfStockBadge ref_={p.reference} alerts={p.restock_alert_count} />}</td>
                <td className="px-4 py-2 text-muted-foreground">{p.category}</td>
                <td className="px-4 py-2 text-right">${p.price_dozen.toFixed(2)}</td>
                <td className="px-4 py-2 text-right">{p.packing} {p.packing_unit || "doc"}</td>
                {incompleteOnly && <td className="px-4 py-2"><MissingChips fields={p.missing_fields} ref_={p.reference} /></td>}
                <td className="px-4 py-2">
                  <ProductActions p={p} openEdit={openEdit} remove={remove} toggleStock={toggleStock} onImage={() => { setImgTarget(p.id); fileRef.current?.click(); }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && <p className="text-center py-10 text-muted-foreground text-sm">Sin productos. Use "Importar Drive" para cargar el catálogo.</p>}
      </div>

      <div className="md:hidden space-y-3">
        {products.map((p) => (
          <div key={p.id} className="bg-card border border-border rounded-2xl p-3 flex gap-3" data-testid={`admin-product-card-${p.reference}`}>
            {p.image_url ? <img src={imgUrl(p.image_url)} alt="" className="w-16 h-16 rounded-xl object-cover" /> : <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center"><ImageOff size={16} /></div>}
            <div className="flex-1 min-w-0">
              <span className="text-[10px] font-mono text-amber-600 uppercase">{p.reference}</span>
              <p className="font-medium text-sm">{p.name} {p.out_of_stock && <OutOfStockBadge ref_={p.reference} alerts={p.restock_alert_count} />}</p>
              <p className="text-xs text-muted-foreground">{p.category} · ${p.price_dozen.toFixed(2)}/{p.packing_unit || "doc"} · {p.packing} {p.packing_unit || "doc"}/caja</p>
              {incompleteOnly && <div className="pt-1"><MissingChips fields={p.missing_fields} ref_={p.reference} /></div>}
              <div className="pt-1"><ProductActions p={p} openEdit={openEdit} remove={remove} toggleStock={toggleStock} onImage={() => { setImgTarget(p.id); fileRef.current?.click(); }} /></div>
            </div>
          </div>
        ))}
      </div>

      <input type="file" accept="image/*" multiple ref={fileRef} className="hidden" data-testid="product-image-file-input"
        onChange={(e) => { uploadImage(Array.from(e.target.files)); e.target.value = ""; }} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nuevo producto" : "Editar producto"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            {[["reference", "Referencia", "text"], ["name", "Nombre del producto", "text"], ["price_dozen", "Precio por unidad de empaque ($)", "number"], ["packing", "Empaque (cantidad por caja)", "number"], ["size", "Tamaño", "text"], ["weight_kg", "Peso (kg)", "number"], ["cubic_ft", "Cubicaje (pie³)", "number"]].map(([k, label, type]) => (
              <div key={k}>
                <label className="text-sm font-medium mb-1 block">{label}</label>
                <input required={k === "reference" || k === "name"} type={type} step="0.01" value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`product-${k}-input`}
                  className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              </div>
            ))}
            <div>
              <label className="text-sm font-medium mb-1 block">Categoría</label>
              <select value={catOther ? "__other__" : form.category}
                onChange={(e) => { const v = e.target.value; if (v === "__other__") { setCatOther(true); setForm({ ...form, category: "" }); } else { setCatOther(false); setForm({ ...form, category: v }); } }}
                data-testid="product-category-select"
                className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
                <option value="">Seleccione una categoría</option>
                {[...categories].sort((a, b) => a.localeCompare(b)).map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__other__">OTRA (ingresar manualmente)</option>
              </select>
              {catOther && (
                <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value.toUpperCase() })}
                  data-testid="product-category-other-input" placeholder="Escriba la nueva categoría"
                  className="mt-2 w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Tipo de empaque</label>
              <div className="flex gap-2" data-testid="packing-unit-selector">
                {[["doc", "Docenas (doc)"], ["pzs", "Piezas (pzs)"], ["set", "Sets (set)"]].map(([u, label]) => (
                  <button type="button" key={u} onClick={() => setForm({ ...form, packing_unit: u })} data-testid={`packing-unit-${u}`}
                    className={`flex-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${form.packing_unit === u ? "bg-slate-900 dark:bg-sky-500 text-white border-transparent" : "border-border hover:bg-muted"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {editing === "new" && (
            <div>
              <label className="text-sm font-medium mb-1 block">Imagen del producto</label>
              <div className="flex items-center gap-3">
                {(dialogPreview || (editing !== "new" && editing?.image_url)) ? (
                  <img src={dialogPreview || imgUrl(editing.image_url)} alt="preview"
                    data-testid="product-image-preview"
                    className="w-16 h-16 rounded-xl object-cover border border-border" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center border border-border"><ImageOff size={18} /></div>
                )}
                <label className="flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted cursor-pointer">
                  <Upload size={14} className="text-amber-600" /> {dialogFile ? dialogFile.name.slice(0, 22) : "Seleccionar imagen"}
                  <input type="file" accept="image/*" className="hidden" data-testid="product-dialog-image-input"
                    onChange={(e) => { pickDialogFile(e.target.files[0]); e.target.value = ""; }} />
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Se ajusta automáticamente a 600x600 px y máx. 100 KB.</p>
            </div>
            )}
            {editing !== "new" && (
              <div data-testid="product-gallery">
                <label className="text-sm font-medium mb-1 block">Galería de fotos ({galleryImages.length})</label>
                {galleryImages.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Este producto no tiene fotos todavía.</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {galleryImages.map((u, i) => (
                      <div key={u} draggable
                        onDragStart={() => setDragIdx(i)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => reorderGallery(dragIdx, i)}
                        onDragEnd={() => setDragIdx(null)}
                        className={`relative group cursor-move rounded-xl transition-opacity ${dragIdx === i ? "opacity-40 ring-2 ring-amber-500" : ""}`}
                        data-testid={`gallery-item-${i}`}>
                        <img src={imgUrl(u)} alt="" className="w-full aspect-square rounded-xl object-cover border border-border pointer-events-none" />
                        {i === 0 && <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-amber-500 text-white text-[9px] font-bold">Principal</span>}
                        <button type="button" onClick={() => deleteGalleryImage(u)} data-testid={`delete-gallery-image-${i}`}
                          className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-90 hover:bg-red-600 shadow">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted cursor-pointer">
                  <Plus size={14} className="text-amber-600" /> Agregar foto a la galería
                  <input type="file" accept="image/*" multiple className="hidden" data-testid="gallery-add-input"
                    onChange={(e) => { Array.from(e.target.files).forEach(addGalleryImage); e.target.value = ""; }} />
                </label>
                <p className="text-xs text-muted-foreground mt-1">Arrastra las fotos para reordenarlas: la <b>primera</b> es la imagen principal del catálogo. Borrar una foto reasigna la principal automáticamente.</p>
              </div>
            )}
            <button type="submit" disabled={saving} data-testid="product-save-button"
              className="w-full py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold disabled:opacity-50">{saving ? "Guardando..." : "Guardar"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MISSING_LABELS = { price_dozen: "Precio", packing: "Empaque", size: "Tamaño", weight_kg: "Peso (kg)", cubic_ft: "Cubicaje (pie³)", image_url: "Imagen" };
function MissingChips({ fields = [], ref_ }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={`missing-fields-${ref_}`}>
      {fields.map((f) => <span key={f} className="px-2 py-0.5 rounded-md bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px] font-bold">{MISSING_LABELS[f] || f}</span>)}
    </div>
  );
}

function OutOfStockBadge({ ref_, alerts }) {
  return (
    <>
      <span data-testid={`out-of-stock-badge-${ref_}`} className="ml-1 inline-block align-middle px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider">AGOTADO</span>
      {alerts > 0 && (
        <span data-testid={`restock-alert-count-${ref_}`} title="Clientes esperando reposición"
          className="ml-1 inline-flex items-center gap-1 align-middle px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] font-bold">
          <Bell size={10} /> {alerts} {alerts === 1 ? "cliente espera" : "clientes esperan"}
        </span>
      )}
    </>
  );
}

function ProductActions({ p, openEdit, remove, onImage, toggleStock }) {
  return (
    <div className="flex items-center gap-1 justify-end md:justify-end">
      <button onClick={() => toggleStock(p)} data-testid={`toggle-stock-${p.reference}`}
        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${p.out_of_stock ? "border-emerald-500 text-emerald-600 hover:bg-emerald-500/10" : "border-red-400 text-red-600 hover:bg-red-500/10"}`}>
        {p.out_of_stock ? <><PackageCheck size={13} /> Marcar como Disponible</> : <><PackageX size={13} /> Marcar como Agotado</>}
      </button>
      <button onClick={onImage} title="Subir imagen" data-testid={`upload-image-${p.reference}`} className="p-2 hover:bg-muted rounded-full"><Upload size={14} /></button>
      <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.reference}`} className="p-2 hover:bg-muted rounded-full"><Pencil size={14} /></button>
      <button onClick={() => remove(p)} data-testid={`delete-product-${p.reference}`} className="p-2 text-muted-foreground hover:text-red-500 rounded-full"><Trash2 size={14} /></button>
    </div>
  );
}

function ProformasTab() {
  const [proformas, setProformas] = useState([]);
  const [detail, setDetail] = useState(null);
  const [fSearch, setFSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const PF_STATUS = {
    pendiente: { label: "Pendiente", cls: "bg-amber-500/15 text-amber-600" },
    confirmada: { label: "Confirmada", cls: "bg-sky-500/15 text-sky-600" },
    despachada: { label: "Despachada", cls: "bg-emerald-500/15 text-emerald-600" },
    anulada: { label: "Anulada", cls: "bg-rose-500/15 text-rose-600" },
  };

  const setPfStatus = async (pf, status) => {
    try {
      await api.put(`/admin/proformas/${pf._id}/status`, { status });
      setProformas((list) => list.map((p) => (p._id === pf._id ? { ...p, status } : p)));
      if (detail?._id === pf._id) setDetail({ ...detail, status });
      toast.success(`Proforma ${pf.number}: ${PF_STATUS[status].label}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const StatusSelect = ({ pf }) => (
    <select value={pf.status || "pendiente"} onChange={(e) => setPfStatus(pf, e.target.value)}
      onClick={(e) => e.stopPropagation()} data-testid={`proforma-status-select-${pf.number}`}
      className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-600 ${PF_STATUS[pf.status || "pendiente"].cls}`}>
      {Object.entries(PF_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
  );

  useEffect(() => {
    api.get("/admin/proformas").then(({ data }) => setProformas(data)).catch((e) => toast.error(errDetail(e)));
  }, []);

  const download = async (pf) => {
    try {
      const res = await api.get(`/proformas/${pf._id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proforma_${pf.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("es", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const filtered = proformas.filter((pf) => {
    if (fStatus && (pf.status || "pendiente") !== fStatus) return false;
    if (fSearch) {
      const q = fSearch.toLowerCase();
      if (!pf.client_name.toLowerCase().includes(q) && !pf.email.toLowerCase().includes(q) && !pf.number.toLowerCase().includes(q)) return false;
    }
    const d = pf.created_at.slice(0, 10);
    if (fFrom && d < fFrom) return false;
    if (fTo && d > fTo) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap" data-testid="proformas-filters">
        <input value={fSearch} onChange={(e) => setFSearch(e.target.value)} data-testid="proforma-filter-search"
          placeholder="Buscar cliente, email o N°..."
          className="px-4 py-2 rounded-full border border-input bg-background text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-amber-600" />
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="proforma-filter-status"
          className="px-4 py-2 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
          <option value="">Todos los estados</option>
          {Object.entries(PF_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div className="flex items-center gap-2 text-sm">
          <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="proforma-filter-from"
            className="px-3 py-2 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
          <span className="text-muted-foreground">a</span>
          <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="proforma-filter-to"
            className="px-3 py-2 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
        </div>
        {(fSearch || fStatus || fFrom || fTo) && (
          <button onClick={() => { setFSearch(""); setFStatus(""); setFFrom(""); setFTo(""); }} data-testid="proforma-filter-clear"
            className="text-xs text-amber-600 hover:underline">Limpiar filtros</button>
        )}
      </div>
      <p className="text-sm text-muted-foreground" data-testid="proformas-count">{filtered.length} de {proformas.length} proformas</p>

      <div className="hidden md:block bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-proformas-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">N°</th>
              <th className="px-4 py-3 font-semibold">Cliente</th>
              <th className="px-4 py-3 font-semibold">Fecha</th>
              <th className="px-4 py-3 font-semibold">Estado</th>
              <th className="px-4 py-3 font-semibold text-right">Artículos</th>
              <th className="px-4 py-3 font-semibold text-right">Gran Total</th>
              <th className="px-4 py-3 font-semibold text-right">PDF</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((pf) => (
              <tr key={pf._id} className="border-t border-border hover:bg-muted/40 cursor-pointer" data-testid={`proforma-row-${pf.number}`} onClick={() => setDetail(pf)}>
                <td className="px-4 py-3 font-mono text-xs text-amber-600 underline underline-offset-2">{pf.number}</td>
                <td className="px-4 py-3"><p className="font-medium">{pf.client_name}</p><p className="text-xs text-muted-foreground">{pf.email}</p></td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(pf.created_at)}</td>
                <td className="px-4 py-3"><StatusSelect pf={pf} /></td>
                <td className="px-4 py-3 text-right">{pf.items.length}</td>
                <td className="px-4 py-3 text-right font-semibold">${pf.grand_total.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={(e) => { e.stopPropagation(); download(pf); }} data-testid={`download-proforma-${pf.number}`}
                    className="p-2 hover:bg-muted rounded-full text-amber-600"><FileDown size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center py-10 text-muted-foreground text-sm" data-testid="proformas-empty-state">{proformas.length === 0 ? "Aún no se han generado proformas." : "Sin resultados con los filtros aplicados."}</p>}
      </div>

      <div className="md:hidden space-y-3">
        {filtered.length === 0 && <p className="text-center py-10 text-muted-foreground text-sm">{proformas.length === 0 ? "Aún no se han generado proformas." : "Sin resultados con los filtros aplicados."}</p>}
        {filtered.map((pf) => (
          <div key={pf._id} className="bg-card border border-border rounded-2xl p-4 space-y-1.5" data-testid={`proforma-card-${pf.number}`} onClick={() => setDetail(pf)}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-amber-600 underline underline-offset-2">{pf.number}</span>
              <div className="flex items-center gap-1">
                <StatusSelect pf={pf} />
                <button onClick={(e) => { e.stopPropagation(); download(pf); }} className="p-2 hover:bg-muted rounded-full text-amber-600"><FileDown size={16} /></button>
              </div>
            </div>
            <p className="font-semibold text-sm">{pf.client_name}</p>
            <p className="text-xs text-muted-foreground">{pf.email}</p>
            <p className="text-xs text-muted-foreground">{fmt(pf.created_at)} · {pf.items.length} artículos</p>
            <p className="font-bold">${pf.grand_total.toFixed(2)}</p>
          </div>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="proforma-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-amber-600">{detail?.number}</span> — {detail?.client_name}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{detail.email} · {fmt(detail.created_at)}</p>
              <StatusSelect pf={detail} />
              {detail.notes && (
                <p className="text-sm bg-muted rounded-xl px-4 py-2.5" data-testid="proforma-detail-notes"><b>Notas:</b> {detail.notes}</p>
              )}
              <div className="border border-border rounded-xl overflow-hidden max-h-[50vh] overflow-y-auto">
                <table className="w-full text-sm" data-testid="proforma-detail-items">
                  <thead className="bg-muted/50 text-left sticky top-0">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Ref.</th>
                      <th className="px-3 py-2 font-semibold">Producto</th>
                      <th className="px-3 py-2 font-semibold text-right">Cajas</th>
                      <th className="px-3 py-2 font-semibold text-right">Cantidad</th>
                      <th className="px-3 py-2 font-semibold text-right">Precio/ud</th>
                      <th className="px-3 py-2 font-semibold text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-2 font-mono text-xs">{it.reference}</td>
                        <td className="px-3 py-2">{it.name}<p className="text-xs text-muted-foreground">{it.category}</p></td>
                        <td className="px-3 py-2 text-right">{it.qty_boxes ?? it.qty_dozens}</td>
                        <td className="px-3 py-2 text-right">{it.units ?? it.qty_dozens} {it.unit || "doc"}</td>
                        <td className="px-3 py-2 text-right">${it.price_dozen.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-semibold">${it.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between">
                <button onClick={() => download(detail)} data-testid="proforma-detail-download"
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-muted">
                  <FileDown size={15} className="text-amber-600" /> Descargar PDF
                </button>
                <p className="text-lg font-bold" data-testid="proforma-detail-total">Gran Total: ${detail.grand_total.toFixed(2)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportTab() {
  const [status, setStatus] = useState({ running: false, log: [] });
  const [sheetUrl, setSheetUrl] = useState("");
  const pollRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/import/status");
      setStatus(data);
      if (!data.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch {}
  }, []);

  useEffect(() => { poll(); return () => pollRef.current && clearInterval(pollRef.current); }, [poll]);

  const startImport = async () => {
    try {
      await api.post("/admin/import/drive");
      toast.success("Importación iniciada desde Google Drive");
      setStatus((s) => ({ ...s, running: true }));
      pollRef.current = setInterval(poll, 3000);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const importSheet = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/import/sheet", { sheet_url: sheetUrl });
      toast.success(`${data.imported} productos importados desde la hoja`);
      setSheetUrl("");
    } catch (err) { toast.error(errDetail(err)); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <h3 className="font-semibold font-heading flex items-center gap-2"><CloudDownload size={18} className="text-amber-600" /> Importación completa desde Google Drive</h3>
        <p className="text-sm text-muted-foreground">
          Escanea la carpeta raíz <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">16sgNR3aovlx...922X</code>: cada subcarpeta es una categoría y cada imagen crea/actualiza un producto (referencia = nombre del archivo). Las imágenes se comprimen automáticamente a máx. 100 KB y 600x600 px. Si hay un archivo de catálogo (Sheets/CSV) en la raíz, también se importan precios y empaques.
        </p>
        <button onClick={startImport} disabled={status.running} data-testid="admin-google-drive-import-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
          <CloudDownload size={16} /> {status.running ? "Importando..." : "Importar desde Drive"}
        </button>
        {status.log.length > 0 && (
          <div className="bg-slate-950 text-slate-200 rounded-xl p-3 max-h-64 overflow-y-auto font-mono text-xs space-y-1" data-testid="import-log">
            {status.log.map((l, i) => <p key={i}>{l}</p>)}
            {status.running && <p className="animate-pulse">▋ Procesando...</p>}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <h3 className="font-semibold font-heading">Actualizar catálogo con Google Sheets</h3>
        <p className="text-sm text-muted-foreground">Pegue la URL de una hoja pública con columnas: referencia, nombre, categoría, precio, empaque. Actualiza precios y datos por referencia.</p>
        <form onSubmit={importSheet} className="flex flex-col sm:flex-row gap-2">
          <input required value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} data-testid="sheet-url-input"
            placeholder="https://docs.google.com/spreadsheets/d/..."
            className="flex-1 px-4 py-2.5 rounded-full border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
          <button type="submit" data-testid="import-sheet-button"
            className="px-5 py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold">Importar hoja</button>
        </form>
      </div>
    </div>
  );
}

function SettingsTab() {
  const { settings, refreshSettings } = useAuth();
  const [name, setName] = useState(settings.platform_name);
  useEffect(() => setName(settings.platform_name), [settings.platform_name]);
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const loadBackups = useCallback(() => api.get("/admin/backups").then(({ data }) => setBackups(data)).catch(() => {}), []);
  useEffect(() => { loadBackups(); }, [loadBackups]);
  useEffect(() => {
    if (!backups.some((b) => b.status === "en_proceso")) return;
    const id = setInterval(loadBackups, 3000);
    return () => clearInterval(id);
  }, [backups, loadBackups]);

  const createBackup = async () => {
    setBackupBusy(true);
    try {
      await api.post("/admin/backup");
      toast.success("Backup iniciado. Te avisaremos por correo al completarse.");
      loadBackups();
    } catch (e) { toast.error(errDetail(e)); }
    setBackupBusy(false);
  };

  const downloadBackup = async (b) => {
    try {
      const res = await api.get(`/admin/backups/${b.filename}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = b.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const fmt = (iso) => new Date(iso).toLocaleString("es-PA", { timeZone: "America/Panama", dateStyle: "short", timeStyle: "short" });
  const mb = (n) => `${(n / 1048576).toFixed(1)} MB`;
  const restoreRef = useRef(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreInfo, setRestoreInfo] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);
  const uploadRestore = async (file) => {
    if (!file) return;
    setRestoreBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/restore/preview", fd);
      setRestoreFile(file); setRestoreInfo(data); setRestoreConfirm("");
      toast.success("Backup verificado. Revisa el contenido y confirma la restauración.");
    } catch (e) { toast.error(errDetail(e)); }
    setRestoreBusy(false);
  };
  const doRestore = async () => {
    setRestoreBusy(true);
    try {
      const fd = new FormData(); fd.append("file", restoreFile); fd.append("confirm", restoreConfirm);
      const { data } = await api.post("/admin/restore", fd);
      toast.success(`Restauración completada: ${data.collections.length} colecciones, ${data.images} imágenes`);
      setRestoreInfo(null); setRestoreFile(null); setRestoreConfirm("");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(errDetail(e)); }
    setRestoreBusy(false);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", { platform_name: name });
      await refreshSettings();
      toast.success("Nombre de la plataforma actualizado");
    } catch (err) { toast.error(errDetail(err)); }
  };

  return (
    <div className="space-y-6">
    <form onSubmit={save} className="bg-card border border-border rounded-2xl p-5 space-y-4 max-w-lg">
      <h3 className="font-semibold font-heading">Nombre de la plataforma</h3>
      <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="platform-name-input"
        className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
      <button type="submit" data-testid="save-settings-button"
        className="px-5 py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold">Guardar</button>
    </form>

    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="backup-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold font-heading flex items-center gap-2"><DatabaseBackup size={18} /> Backup manual</h3>
          <p className="text-xs text-muted-foreground mt-1">Exporta toda la base de datos y las imágenes de productos en un ZIP con fecha y hora, lo sube a Google Drive y notifica por correo.</p>
        </div>
        <button onClick={createBackup} disabled={backupBusy || backups.some((b) => b.status === "en_proceso")} data-testid="create-backup-button"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap">
          <DatabaseBackup size={16} /> Crear Backup
        </button>
      </div>
      {backups.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="backups-empty">Aún no hay backups.</p> : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="backups-table">
            <thead className="bg-muted/60 text-left"><tr><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Archivo</th><th className="px-3 py-2">Tamaño</th><th className="px-3 py-2">Drive</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2"></th></tr></thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b._id} className="border-t border-border" data-testid={`backup-row-${b.filename}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(b.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs break-all">{b.filename}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{b.size ? mb(b.size) : "—"}</td>
                  <td className="px-3 py-2">
                    {b.drive_link ? <a href={b.drive_link} target="_blank" rel="noreferrer" className="text-sky-600 hover:underline" data-testid={`backup-drive-link-${b.filename}`}>Ver en Drive</a>
                      : <span className="text-xs text-muted-foreground" title={b.drive_error || ""}>{b.drive_error ? "No subido" : "—"}</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span title={b.error || b.drive_error || ""} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${b.status === "completado" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : b.status === "en_proceso" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}>
                      {b.status === "completado" ? "Completado" : b.status === "en_proceso" ? "En proceso…" : "Error"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {b.status === "completado" && <button onClick={() => downloadBackup(b)} data-testid={`backup-download-${b.filename}`} className="p-2 hover:bg-muted rounded-full" title="Descargar"><FileDown size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    <div className="bg-card border border-red-200 dark:border-red-900 rounded-2xl p-5 space-y-4" data-testid="restore-section">
      <div>
        <h3 className="font-semibold font-heading flex items-center gap-2 text-red-700 dark:text-red-300"><RotateCcw size={18} /> Restaurar backup</h3>
        <p className="text-xs text-muted-foreground mt-1">Sube un archivo .zip de backup. Al confirmar, la base de datos y las imágenes actuales serán <b>reemplazadas</b> por las del backup.</p>
      </div>
      <input type="file" accept=".zip" ref={restoreRef} className="hidden" data-testid="restore-file-input"
        onChange={(e) => { uploadRestore(e.target.files[0]); e.target.value = ""; }} />
      <button onClick={() => restoreRef.current?.click()} disabled={restoreBusy} data-testid="restore-upload-button"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-input text-sm font-semibold hover:bg-muted disabled:opacity-50">
        <Upload size={16} /> {restoreBusy && !restoreInfo ? "Subiendo…" : "Subir archivo de backup"}
      </button>
      {restoreInfo && (
        <div className="space-y-3 border-t border-border pt-3" data-testid="restore-preview">
          <p className="text-sm"><b>{restoreInfo.filename}</b> · {mb(restoreInfo.size)} · {restoreInfo.collections.length} colecciones · {restoreInfo.images} imágenes</p>
          <p className="text-xs text-muted-foreground break-words">{restoreInfo.collections.join(", ")}</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input value={restoreConfirm} onChange={(e) => setRestoreConfirm(e.target.value)} placeholder='Escribe RESTAURAR para confirmar' data-testid="restore-confirm-input"
              className="px-4 py-2.5 rounded-xl border border-input bg-background text-sm w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-red-500" />
            <button onClick={doRestore} disabled={restoreBusy || restoreConfirm.trim().toUpperCase() !== "RESTAURAR"} data-testid="restore-confirm-button"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
              <RotateCcw size={16} /> {restoreBusy ? "Restaurando…" : "Confirmar restauración"}
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
